import { FC } from 'react';
import { Alert, Button, Grid } from '@mui/material';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import BaseConfig from './BaseConfig';
import { useCreatePaymentMethodMutation } from '../../../stores/FetcherStores/PaymentMethodFetcher';
import useCompanyId from '../../../hooks/useCompanyId';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  DefaultShortNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../../model/PaymentMethodConfig';
import { NetsTerminalPaymentMethod } from '../../../model/PaymentMethod';
import { usePaymentMethodForm } from '../../paymethod/fields/usePaymentMethodForm';
import SharedPaymentMethodFieldGroup from '../../paymethod/fields/SharedPaymentMethodFieldGroup';
import NetsFieldGroup from '../../paymethod/fields/NetsFieldGroup';
import { usePrices, useResellerPrices } from '../../../stores/FetcherStores/PricesFetcher';
import { useGetCompany } from '../../../stores/FetcherStores/CompanyFetcher';

interface NetsConfigProps {
  onClose: () => void;
}

const NetsConfig: FC<NetsConfigProps> = observer(({ onClose }) => {
  const companyId = useCompanyId();
  const { data: company } = useGetCompany(companyId);
  const {
    mutateAsync: createPaymentMethod,
    isLoading: isCreating,
    error,
  } = useCreatePaymentMethodMutation();
  const { data: prices } = usePrices();
  const { data: resellerPrices } = useResellerPrices(company?.resellerId || '');

  const form = usePaymentMethodForm({
    defaultValues: {
      baseFields: {
        name: DefaultNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.NETS_TERMINAL],
        shortName:
          DefaultShortNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.NETS_TERMINAL],
        active: true,
        bookkeepingAccount: '',
      },
      netsFields: {
        terminalId: '',
        username: '',
        password: '',
        boughtViaEnklakassan: false,
        acquirer: '',
        acquirerNumber: '',
        mccCode: '',
      },
    },
    onSubmit: async ({ value }) => {
      const newPaymentMethod: Omit<NetsTerminalPaymentMethod, 'id'> = {
        type: PaymentMethodConfigTypes.NETS_TERMINAL,
        name: value.baseFields.name,
        shortName: value.baseFields.shortName,
        active: value.baseFields.active,
        bookkeepingAccount: value.baseFields.bookkeepingAccount,
        terminalId: value.netsFields.terminalId,
        username: value.netsFields.username,
        password: value.netsFields.password,
        boughtViaEnklakassan: value.netsFields.boughtViaEnklakassan,
        acquirer: value.netsFields.acquirer,
        acquirerNumber: value.netsFields.acquirerNumber,
        mccCode: value.netsFields.mccCode,
      };
      await createPaymentMethod(companyId, newPaymentMethod)
        .then(() => {
          onClose();
        })
        .catch(() => {});
    },
  });

  return (
    <BaseConfig
      title="Kortbetalning - Nets"
      description="Koppla en Nets Lane 3000 eller Move 3600 till kassan. Konfigurera dina Nets-inställningar för att komma igång med kortbetalningar."
      price={`${prices?.recurring.NETS_TERMINAL.price.format()}/mån/terminal`}
      resellerPrice={
        resellerPrices
          ? `${resellerPrices?.recurring.NETS_TERMINAL.price.format()}/mån/terminal`
          : ''
      }
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      errorMessage={
        error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message
      }
      buttons={
        <>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={form.state.isSubmitting || isCreating}
            sx={{
              backgroundColor: '#1db954',
              '&:hover': {
                backgroundColor: '#1a9f4a',
              },
            }}
          >
            {form.state.isSubmitting || isCreating ? 'Aktiverar...' : 'Aktivera Nets'}
          </Button>
        </>
      }
    >
      <Grid item xs={12}>
        <Alert severity="info">
          Enklakassan tar inte ut någon månadsavgift för terminaler som är köpta direkt via oss.
          Kontakta oss sa berättar vi mer.
        </Alert>
      </Grid>
      <form.AppForm>
        <SharedPaymentMethodFieldGroup form={form} fields="baseFields" />
        <NetsFieldGroup form={form} fields="netsFields" />
      </form.AppForm>
    </BaseConfig>
  );
});

export default NetsConfig;
